import { RefetchQueryDescriptor, useApolloClient } from "@apollo/client";
import OneSchemaImporter from "@oneschema/react";
import { ImportButton } from "components/ImportButton";
import { isGatedOneschemaTemplate } from "encamp-shared/src/oneSchema/gatedTemplates";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { gql } from "generated-graphql";
import { ImportChangeSummary, OneSchemaImportProcessingMethod } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";

import { useAlerts } from "../Alerts/AlertProvider";
import { ConfirmImportDialog } from "./ConfirmImportDialog";
import { ImportMenuItem } from "./ImportMenuItem";

// isGatedOneschemaTemplate is the single source of truth (encamp-shared).
const isGatedTemplateType = isGatedOneschemaTemplate;

export interface ImporterProps {
  templateType: OneSchemaTemplateType;
  refetchQueries?: RefetchQueryDescriptor[];
  label?: string;
  style?: React.CSSProperties;
  processingMethod?: OneSchemaImportProcessingMethod;
  postProcessingFunction?: (
    handleSuccessfulImport: (message: string) => Promise<void>,
    oneSchemaImportId: string
  ) => Promise<void>;
  mode?: "button" | "dropdown";
  /**
   * Explicit tenant scope. When set, overrides the route's `:tenantId` param —
   * needed on cross-route pages (e.g. the Staff State Waste Streams grid) that
   * are not under a tenant route.
   */
  tenantId?: string;
}

export const INITIALIZE_IMPORT = gql(`
  mutation InitializeOneSchemaImport($tenantId: String, $templateType: String!, $processingMethod: OneSchemaImportProcessingMethod!) {
    initializeOneSchemaImport(tenantId: $tenantId, templateType: $templateType, processingMethod: $processingMethod) {
      oneSchemaImportId
      clientId
      sessionToken
    }
  }
`);

export const PROCESS_ONESCHEMA_IMPORT = gql(`
  mutation ProcessOneSchemaImport($input: ProcessOneschemaFileUploadInput!) {
    processOneschemaFileUpload(input: $input)
  }
`);

export const PREVIEW_ONESCHEMA_IMPORT = gql(`
  mutation PreviewOneschemaImport($oneSchemaImportId: String!) {
    previewOneschemaImport(oneSchemaImportId: $oneSchemaImportId) {
      entityLabel
      recordsAffected
      added
      edited
      deleted
    }
  }
`);

export const COMMIT_ONESCHEMA_IMPORT = gql(`
  mutation CommitOneschemaImport($oneSchemaImportId: String!) {
    commitOneschemaImport(oneSchemaImportId: $oneSchemaImportId)
  }
`);

export const CANCEL_ONESCHEMA_IMPORT = gql(`
  mutation CancelOneschemaImport($oneSchemaImportId: String!) {
    cancelOneschemaImport(oneSchemaImportId: $oneSchemaImportId)
  }
`);

type ImportSession = {
  oneSchemaImportId: string;
  clientId: string;
  sessionToken: string;
};

type PreviewData = Pick<
  ImportChangeSummary,
  "entityLabel" | "recordsAffected" | "added" | "edited" | "deleted"
>;

type GatedConfirmState = {
  confirmOpen: boolean;
  previewLoading: boolean;
  confirmLoading: boolean;
  preview: PreviewData | null;
  handleSuccessGated: (oneSchemaImportId: string) => Promise<void>;
  handleConfirmImport: () => Promise<void>;
  handleCancelImport: () => Promise<void>;
};

function useGatedConfirm(
  refetchQueries: RefetchQueryDescriptor[],
  onSuccess: () => void
): GatedConfirmState {
  const apolloClient = useApolloClient();
  const alerts = useAlerts();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [pendingImportId, setPendingImportId] = useState<string | null>(null);

  const resetConfirm = useCallback(() => {
    setConfirmOpen(false);
    setPreview(null);
    setPendingImportId(null);
    setPreviewLoading(false);
  }, []);

  const cancelStagedImport = useCallback(
    async (oneSchemaImportId: string) => {
      try {
        await apolloClient.mutate({
          mutation: CANCEL_ONESCHEMA_IMPORT,
          variables: { oneSchemaImportId },
        });
      } catch (error) {
        // Best-effort cleanup; the stale-import sweep reclaims anything left behind.
        console.error("Error cancelling import:", error);
      }
    },
    [apolloClient]
  );

  const handleSuccessGated = useCallback(
    async (oneSchemaImportId: string) => {
      // Open the dialog immediately in a loading state so there is no blank gap
      // while the preview counts are computed for a large import.
      setPendingImportId(oneSchemaImportId);
      setPreview(null);
      setPreviewLoading(true);
      setConfirmOpen(true);
      try {
        const { data } = await apolloClient.mutate({
          mutation: PREVIEW_ONESCHEMA_IMPORT,
          variables: { oneSchemaImportId },
        });
        const previewResult = data?.previewOneschemaImport;
        if (!previewResult) {
          throw new Error("Failed to preview OneSchema import");
        }

        // Nothing to apply — clean up the staged import and skip the empty dialog.
        if (previewResult.added + previewResult.edited + previewResult.deleted === 0) {
          await cancelStagedImport(oneSchemaImportId);
          resetConfirm();
          alerts.info("No changes to import — your spreadsheet matched the existing data.");
          return;
        }

        setPreview(previewResult);
      } catch (error) {
        console.error("Error previewing import:", error);
        alerts.error("Failed to preview the import. Please try again.");
        await cancelStagedImport(oneSchemaImportId);
        resetConfirm();
      } finally {
        setPreviewLoading(false);
      }
    },
    [alerts, apolloClient, cancelStagedImport, resetConfirm]
  );

  const handleConfirmImport = useCallback(async () => {
    if (!pendingImportId) return;
    setConfirmLoading(true);
    try {
      await apolloClient.mutate({
        mutation: COMMIT_ONESCHEMA_IMPORT,
        variables: { oneSchemaImportId: pendingImportId },
      });

      alerts.success("Import committed successfully.");
      if (refetchQueries.length) {
        await apolloClient.refetchQueries({ include: refetchQueries });
      }
      resetConfirm();
      onSuccess();
    } catch (error) {
      // A failed commit leaves the import in FAILED (no longer confirmable), so
      // close the dialog and direct the user to re-run rather than leaving a dead
      // Confirm button. The server clears the staged rows on the FAILED transition.
      console.error("Error committing import:", error);
      alerts.error("Failed to commit the import. Please re-run the import to try again.");
      resetConfirm();
    } finally {
      setConfirmLoading(false);
    }
  }, [alerts, apolloClient, onSuccess, pendingImportId, refetchQueries, resetConfirm]);

  const handleCancelImport = useCallback(async () => {
    if (pendingImportId) {
      await cancelStagedImport(pendingImportId);
    }
    resetConfirm();
  }, [cancelStagedImport, pendingImportId, resetConfirm]);

  return {
    confirmOpen,
    previewLoading,
    confirmLoading,
    preview,
    handleSuccessGated,
    handleConfirmImport,
    handleCancelImport,
  };
}

export function Importer({
  templateType,
  refetchQueries = [],
  label,
  style,
  processingMethod = OneSchemaImportProcessingMethod.Webhook,
  mode = "button",
  tenantId: tenantIdProp,
}: ImporterProps) {
  const { tenantId: routeTenantId } = useParams();
  const tenantId = tenantIdProp ?? routeTenantId;
  const alerts = useAlerts();
  const apolloClient = useApolloClient();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [session, setSession] = useState<ImportSession | null>(null);

  const handleCloseImporter = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleGatedSuccess = useCallback(() => {
    setIsSuccess(true);
    setSession(null);
  }, []);

  const gated = useGatedConfirm(refetchQueries, handleGatedSuccess);

  const onSuccess = useCallback(async () => {
    if (!session?.oneSchemaImportId) {
      throw new Error("No active OneSchema import session");
    }

    if (isGatedTemplateType(templateType)) {
      handleCloseImporter();
      // Archive the uploaded/clean source files to S3, same as the non-gated path.
      // The file-upload lambda no longer sets status for gated imports, so this is
      // archival-only and runs independently of the confirm/commit flow. Best-effort:
      // never block the gated import on archival.
      try {
        await apolloClient.mutate({
          mutation: PROCESS_ONESCHEMA_IMPORT,
          variables: {
            input: {
              tenantId: tenantId ?? null,
              oneSchemaImportId: session.oneSchemaImportId,
            },
          },
        });
      } catch (error) {
        console.error("Failed to enqueue import file archival:", error);
      }
      await gated.handleSuccessGated(session.oneSchemaImportId);
      return;
    }

    const { data } = await apolloClient.mutate({
      mutation: PROCESS_ONESCHEMA_IMPORT,
      variables: {
        input: {
          tenantId: tenantId ?? null,
          oneSchemaImportId: session.oneSchemaImportId,
        },
      },
    });

    const finalizeResult = data?.processOneschemaFileUpload;
    if (!finalizeResult) {
      throw new Error("Failed to finalize OneSchema import");
    }

    alerts.success(`Successfully created job: ${finalizeResult}`);
    if (refetchQueries.length) {
      await apolloClient.refetchQueries({ include: refetchQueries });
    }
    setIsSuccess(true);
    handleCloseImporter();
  }, [
    alerts,
    apolloClient,
    gated,
    handleCloseImporter,
    refetchQueries,
    session?.oneSchemaImportId,
    templateType,
    tenantId,
  ]);

  const handleOpenImporter = async () => {
    setIsLoading(true);
    try {
      const { data } = await apolloClient.mutate({
        mutation: INITIALIZE_IMPORT,
        variables: { tenantId, templateType, processingMethod },
      });
      const init = data?.initializeOneSchemaImport;
      if (!init) throw new Error("Failed to initialize importer");

      setSession({
        oneSchemaImportId: init.oneSchemaImportId,
        clientId: init.clientId,
        sessionToken: init.sessionToken,
      });

      setIsOpen(true);
    } catch (error) {
      console.error("Error opening importer:", error);
      alerts.error("Failed to open importer. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onLaunched = () => {
    setIsLoading(false);
  };

  const onError = (message: unknown) => {
    setIsLoading(false);
    console.error(message);
    alerts.error("An error occurred during the import");
  };

  const importer = isOpen && session && (
    <OneSchemaImporter
      isOpen={isOpen}
      onRequestClose={handleCloseImporter}
      clientId={session.clientId}
      sessionToken={session.sessionToken}
      devMode={import.meta.env.MODE === "development"}
      className="oneschema-importer"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 1300,
      }}
      onLaunched={onLaunched}
      onSuccess={onSuccess}
      onError={onError}
      // Don't save sessions because we'll lose our tracking metadata
      saveSession={false}
    />
  );

  const disabled = isOpen || isLoading;

  return (
    <>
      {/* Portal the full-viewport overlay to <body> so it escapes any ancestor
          stacking context. Rendered inline (e.g. the Staff grid page), the
          overlay lives inside the main content region, which sets its own
          z-index and traps the overlay beneath the left nav; modal-launched
          callers avoid this only because their Dialog already portals to
          <body>. Portaling here makes every caller behave the same. */}
      {importer && createPortal(importer, document.body)}
      {gated.confirmOpen && (
        <ConfirmImportDialog
          open={gated.confirmOpen}
          preview={gated.preview}
          previewLoading={gated.previewLoading}
          onConfirm={gated.handleConfirmImport}
          onCancel={gated.handleCancelImport}
          loading={gated.confirmLoading}
        />
      )}
      {mode === "button" && (
        <ImportButton
          loading={isLoading}
          disabled={disabled}
          success={isSuccess}
          onClick={handleOpenImporter}
          label={label}
          style={{ ...style }}
        />
      )}
      {mode === "dropdown" && (
        <ImportMenuItem
          loading={isLoading}
          disabled={disabled}
          success={isSuccess}
          onClick={handleOpenImporter}
          label={label}
          style={{ ...style }}
        />
      )}
    </>
  );
}
